// Angular
import { ChangeDetectorRef, Component, HostListener, Input, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Params, Router } from '@angular/router';

// Libraries
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { Actions, ofActionCompleted, Select, Store } from '@ngxs/store';
import { GridsterItem } from 'angular-gridster2';
import { Chart } from 'angular-highcharts';
import { addMinutes, differenceInMinutes, differenceInSeconds, subMinutes } from 'date-fns';
import { saveAs } from 'file-saver';
import { AnnotationsOptions } from 'highcharts';
import { first, orderBy, takeRight } from 'lodash-es';
import { map, Observable, Subject, Subscription, takeUntil } from 'rxjs';

// Models
import { UnitPeriodChannelDto } from '../../../modules/channel/models/unit-period-channel-dto.model';
import { UserDashboardSettings } from '../../../modules/channel/models/user-dashboard-settings.model';
import { JobCommentDefaultDto } from '../../../modules/comment/models/job-comment-default-dto';
import { JobCommentDefaultGroupDto } from '../../../modules/comment/models/job-comment-default-group-dto';
import { AutoGenDatesAndAnnotations, CustomChartAttachmentResponse, CustomChartInitModel, CustomChartModel } from '../../../modules/job/components/custom-chart/models/custom-chart-init-model';
import { ChartSettingsModel } from '../../../modules/job/components/custom-chart/models/custom-charts.model';
import { JobDailyReportDto } from '../../../modules/job/components/daily-report/models/job-daily-report-dto';
import { JobTypeDto } from '../../../modules/job/components/daily-report/models/job-type-dto';
import { DashboardDto } from '../../../modules/job/components/dashboard/models/dashboard-dto';
import { DashboardItemDto } from '../../../modules/job/components/dashboard/models/dashboard-item-dto';
import { JobEditDialogOutputData, JobInfoDialogInputData } from '../../../modules/job/dialogs/job-info-dialog/job-info-dialog.service';
import { Job, JobOptions, JobStatus, JobUpdate } from '../../../modules/job/models';
import { JobCommentDto } from '../../../modules/job/models/job-comment-dto';
import { UnitPeriodDto } from '../../../modules/job/models/unit-period-dto.model';
import { ConfirmDialogModel } from '../../dialogs/confirm-dialog/confirm-dialog.component';
import { AppCleanup, DrawerModel, LookUpResponse } from '../../models';

// Services
import { AuthService } from '../../../modules/auth';
import { CompletedJobService } from '../../../modules/job/components/completed-job/services/completed-job.service';
import { CustomChartsInitService } from '../../../modules/job/components/custom-chart/services/custom-charts-init.service';
import { CustomChartsSaveAsAttachmentService } from '../../../modules/job/components/custom-chart/services/custom-charts-save-as-attachment.service';
import { DailyReportService } from '../../../modules/job/components/daily-report/services/daily-report.service';
import { DashboardService } from '../../../modules/job/components/dashboard/services/dashboard.service';
import { CustomChartsService } from '../../../modules/job/services/custom-charts.service';
import { JobService } from '../../../modules/job/services/job.service';
import { CoreService, DateRangeService, GlobalService, SyncService, UtilityService } from '../../services';
import { AudioService } from '../../services/audio.service';
import { DialogService } from '../../services/dialog.service';
import { DrawerService } from '../../services/drawer.service';
import { LayoutConfigService } from '../../services/layout-config.service';
import { SpinnerService } from '../spinner/services/spinner.service';
import { SubheaderService } from './services/subheader.service';

// Components
import { EmailJdrDialogComponent } from '../../../dialogs/email-jdr-dialog/email-jdr-dialog.component';
import { JobChannelMappingDialogComponent } from '../../../dialogs/job-channel-mapping-dialog/job-channel-mapping-dialog.component';
import { RunningFootageDialogComponent } from '../../../dialogs/running-footage-dialog/running-footage-dialog.component';
import { AddDailyReportDialogComponent } from '../../../modules/job/components/daily-report/dialogs/add-daily-report-dialog/add-daily-report-dialog.component';
import { CommentAddDialogComponent } from '../../../modules/job/components/daily-report/dialogs/comment-add-dialog/comment-add-dialog.component';
import { DashboardSettingDialogComponent } from '../../../modules/job/components/dashboard/dialogs/dashboard-setting-dialog/dashboard-setting-dialog.component';
import { SelectDashboardDialogComponent } from '../../../modules/job/components/dashboard/dialogs/select-dashboard-dialog/select-dashboard-dialog.component';
import { WidgetEditDialogComponent } from '../../../modules/job/components/dashboard/dialogs/widget-edit-dialog/widget-edit-dialog.component';
import { JobAlertsEditDialogComponent } from '../../../modules/job/dialogs/job-alerts-edit-dialog/job-alerts-edit-dialog.component';
import { JobEditData, JobEditDialogComponent } from '../../../modules/job/dialogs/job-edit-dialog/job-edit-dialog.component';
import { JobLinksDialogComponent } from '../../../modules/job/dialogs/job-links-dialog/job-links-dialog.component';
import { JobMultiStreamDialogComponent } from '../../../modules/job/dialogs/job-multi-stream-dialog/job-multi-stream-dialog.component';
import { JobSplitMergeDialogComponent } from '../../../modules/job/dialogs/job-split-merge-dialog/job-split-merge-dialog.component';
import { NamingDialogComponent } from '../../dialogs/naming-dialog/naming-dialog.component';

// Enums
import { Roles } from '../../../modules/auth/models/roles.enum';
import { ChangeType } from '../../../modules/job/enums/change-type.enum';
import { StatusType } from '../../enums';

// Config
import { PageConfig, PageViewModel } from '../../config/page.config';

// Helpers
import { DateHelper, UtilityHelper } from '../../helpers';

// Store
import { BopState } from '../../../modules/asset/bop/state/bop.state';
import { DeviceState } from '../../../modules/asset/device/state/device.state';
import { UnitState } from '../../../modules/asset/unit/state/unit.state';
import { AuthState } from '../../../modules/auth/state/auth.state';
import { ChannelState } from '../../../modules/channel/state/channel.state';
import { CompanyState } from '../../../modules/company/state/company.state';
import { DistrictState } from '../../../modules/district/state/district.state';
import { DashboardActions } from '../../../modules/job/components/dashboard/state/dashboard.action';
import { DashboardState } from '../../../modules/job/components/dashboard/state/dashboard.state';
import { JobActions } from '../../../modules/job/state/job.action';
import { JobState } from '../../../modules/job/state/job.state';
import { UnitOfMeasureState } from '../../../modules/unit-of-measure/state/unit-of-measure.state';
import { UserState } from '../../../modules/user/state/user.state';
import { LookupState } from '../../state/lookup.state';
import { StorageActions } from '../../state/storage.action';
import { StorageState } from '../../state/storage.state';
import { TimezoneService } from '../../../modules/job/services/timezone.service';

@Component({
	selector: 'subheader',
	templateUrl: './subheader.component.html',
	styleUrl: './subheader.component.scss',
	providers: [CustomChartsService, CustomChartsInitService, CustomChartsSaveAsAttachmentService],
})
export class SubheaderComponent extends AppCleanup implements OnInit, OnDestroy {
	@Select(AuthState.isLoggedIn) isLoggedIn$: Observable<boolean>;
	@Select(StorageState.commentDrawerSettings) commentDrawerSettings$: Observable<DrawerModel>;
	commentDrawerSettings: DrawerModel;
	@Select(StorageState.modelDrawerSettings) modelDrawerSettings$: Observable<DrawerModel>;
	modelDrawerSettings: DrawerModel;
	@Select(StorageState.currentDashboardId) currentDashboardId$: Observable<number>;
	currentDashboardId: number;
	@Select(StorageState.selectedFilterJobTypeIds) selectedFilterJobTypeIds$: Observable<number[]>;
	selectedFilterJobTypeIds: number[];
	@Select(LookupState.lookups) lookups$: Observable<LookUpResponse>;
	@Select(DashboardState.isLoaded) dashboardsLoaded$: Observable<boolean>;
	@Select(DashboardState.dashboards) dashboards$: Observable<DashboardDto[]>;
	@Select(DashboardState.currentDashboard) currentDashboard$: Observable<DashboardDto>;
	currentDashboard: DashboardDto;
	@Select(StorageState.userDashboardSettings) userDashboardSettings$: Observable<UserDashboardSettings>;
	userDashboardSettings: UserDashboardSettings;
	@Select(JobState.activeJobs) activeJobs$: Observable<Job[]>;
	@Select(JobState.filteredActiveJobs) filteredActiveJobs$: Observable<Job[]>;
	@Select(JobState.cachedJobs) cachedJobs$: Observable<Job[]>;
	@Select(JobState.activeJobsLoaded) activeJobsLoaded$: Observable<boolean>;
	@Select(JobState.completedJobsLoaded) completedJobsLoaded$: Observable<boolean>;
	@Select(CompanyState.isLoaded) companiesLoaded$: Observable<boolean>;
	companiesLoaded: boolean;
	@Select(ChannelState.isLoaded) channelsLoaded$: Observable<boolean>;
	channelsLoaded: boolean;
	@Select(DistrictState.isLoaded) districtsLoaded$: Observable<boolean>;
	districtsLoaded: boolean;
	@Select(UnitOfMeasureState.isLoaded) unitsOfMeasureLoaded$: Observable<boolean>;
	unitsOfMeasureLoaded: boolean;
	@Select(UserState.isLoaded) usersLoaded$: Observable<boolean>;
	usersLoaded: boolean;
	@Select(UnitState.isLoaded) unitsLoaded$: Observable<boolean>;
	unitsLoaded: boolean;
	@Select(BopState.isLoaded) bopsLoaded$: Observable<boolean>;
	bopsLoaded: boolean;
	@Select(DeviceState.isLoaded) devicesLoaded$: Observable<boolean>;
	devicesLoaded: boolean;
	@Select(StorageState.jobRouteParams) jobRouteParams$: Observable<Params>;

	@Input() isPublic: boolean;

	filteredActiveJobs: Job[];
	desc: string = '';
	url: string;
	parentRoute:
		| 'job'
		| 'channel'
		| 'comment'
		| 'company'
		| 'district'
		| 'unit-config'
		| 'output-config'
		| 'asset'
		| 'asset/unit'
		| 'asset/string'
		| 'asset/reel'
		| 'asset/bop'
		| 'asset/device'
		| 'unit'
		| 'unit-of-measure'
		| 'user'
		| 'download'
		| 'version'
		| 'eula'
		| 'error-page'; // Root route
	pageView: string;
	parentViews: PageViewModel[] = new PageConfig().pageViews;
	selectedParentView: PageViewModel;
	showSaveButton: boolean = false;
	job: Job;
	isJobPage: boolean;
	jobCompleted: boolean = false; // Flag for when job is completed
	jobDailyReports: JobDailyReportDto[];
	disabled: boolean;
	showDashboardMenu: boolean;
	searchTerm: string;
	isRoot: boolean = false;
	icon: string = 'flaticon2-bell-alarm-symbol';
	jobTypes: JobTypeDto[] = [];
	showCommentDropdown = false;
	isMobile: boolean = false;
	selectedJobCommentDefaultGroup: JobCommentDefaultGroupDto;
	syncColor: string = 'green';
	syncDiff?: number;
	syncDisplay: string;
	jobId: number;
	dailyReportDate: Date;
	isSuperAdmin: boolean;
	desktopHeaderDisplay: boolean;
	isSplitMergeAvailable$: Observable<boolean>;
	chart: Chart;
	showChart: boolean = false;
	hideTooltip: boolean = true;
	isEditMode: boolean = false;
	jobCommentsOld: JobCommentDto[];
	jobComments: JobCommentDto[];
	forceMonitorReady: boolean; // Flag for when the force monitor page is ready and spinner is off
	showJDRDownload: boolean;
	private syncColorInterval: any; // Store interval reference for cleanup

	readonly JobType = {
		END_PRESSURE_TESTING: 'End pressure testing',
		START_PRESSURE_TESTING: 'Start pressure testing',
	};
	readonly jobSettingsButtonRoles = ['Admin', 'TenantAdmin'];

	@HostListener('window:resize', ['$event'])
	onResize() {
		this.hideTooltip = window.innerWidth > 1200;
	}

	constructor(
		private subheaderService: SubheaderService,
		public dashboardService: DashboardService, // Has to be public since referenced in template
		private dailyReportService: DailyReportService,
		public jobService: JobService, // Has to be public since referenced in template
		private route: ActivatedRoute,
		private router: Router,
		private dialog: MatDialog,
		private changeDetectorRef: ChangeDetectorRef,
		public syncService: SyncService, // Has to be public since referenced in template
		private dateRangeService: DateRangeService,
		private coreService: CoreService,
		private globalService: GlobalService,
		private authService: AuthService,
		private spinnerService: SpinnerService,
		private layoutConfigService: LayoutConfigService,
		private translateService: TranslateService,
		private dialogService: DialogService,
		private timezoneService: TimezoneService,
		private customChartService: CustomChartsService,
		private customChartsInitService: CustomChartsInitService,
		private customChartsSaveAsAttachmentService: CustomChartsSaveAsAttachmentService,
		private utilityService: UtilityService,
		private drawerService: DrawerService,
		private store: Store,
		private actions$: Actions,
		private audioService: AudioService,
		private completedJobService: CompletedJobService
	) {
		super();
		this.url = this.router.url;
		this.mapUrl();

		this.cachedJobs$.pipe(takeUntil(this.destroy)).subscribe((cachedJobs) => {
			if (cachedJobs) {
				let jobsCache = UtilityHelper.deepCopy(cachedJobs);
				this.jobService.jobsCache = jobsCache;
			}
		});
	}

	ngOnInit() {
		this.utilityService.simulateWindowResize();

		// Determines if the device is a mobile device
		this.globalService.isMobile.pipe(takeUntil(this.destroy)).subscribe((isMobile) => {
			this.isMobile = isMobile;
		});

		this.commentDrawerSettings$.pipe(takeUntil(this.destroy)).subscribe((commentDrawerSettings) => {
			if (commentDrawerSettings) {
				this.commentDrawerSettings = commentDrawerSettings;
			}
		});

		this.modelDrawerSettings$.pipe(takeUntil(this.destroy)).subscribe((modelDrawerSettings) => {
			if (modelDrawerSettings) {
				this.modelDrawerSettings = modelDrawerSettings;
			}
		});

		this.currentDashboardId$.pipe(takeUntil(this.destroy)).subscribe((currentDashboardId) => {
			if (currentDashboardId) {
				this.currentDashboardId = currentDashboardId;
			}
		});

		this.userDashboardSettings$.pipe(takeUntil(this.destroy)).subscribe((userDashboardSettings) => {
			if (userDashboardSettings) {
				this.userDashboardSettings = userDashboardSettings;
			}
		});

		this.filteredActiveJobs$.pipe(takeUntil(this.destroy)).subscribe((filteredActiveJobs) => {
			if (filteredActiveJobs) {
				this.filteredActiveJobs = filteredActiveJobs;
			}
		});

		this.lookups$.pipe(takeUntil(this.destroy)).subscribe((lookups) => {
			if (lookups) {
				this.jobTypes = UtilityHelper.deepCopy(lookups.jobTypes);

				this.selectedFilterJobTypeIds$.pipe(takeUntil(this.destroy)).subscribe((selectedFilterJobTypeIds) => {
					if (selectedFilterJobTypeIds) {
						this.selectedFilterJobTypeIds = selectedFilterJobTypeIds;
						this.jobTypes.forEach((jobType) => {
							jobType.checked = selectedFilterJobTypeIds?.includes(jobType.id);
						});

						let jobTypeIds;

						if (!this.isViewer) {
							jobTypeIds = this.jobTypes.filter((jobType) => jobType.checked).map((jobType) => jobType.id);
						} else {
							// There is no filter on viewer so it should show all jobs
							jobTypeIds = [];
						}

						this.activeJobs$.pipe(takeUntil(this.destroy)).subscribe((activeJobs) => {
							if (activeJobs) {
								this.store.dispatch(new JobActions.FilterActiveJobs(jobTypeIds));
							}
						});
					}
				});
			}
		});

		this.dashboards$.pipe(takeUntil(this.destroy)).subscribe((dashboards) => {
			if (dashboards && dashboards.length > 0) {
				this.currentDashboard$.pipe(takeUntil(this.destroy)).subscribe((currentDashboard) => {
					if (currentDashboard) {
						this.currentDashboard = currentDashboard;
					} else {
						let currentDashboard: DashboardDto;
						if (this.currentDashboardId) {
							currentDashboard = dashboards.find((ud) => ud.id === this.currentDashboardId);
						} else {
							currentDashboard = dashboards.find((ud) => ud.id === 1);
						}

						currentDashboard = currentDashboard ?? dashboards[0];
						this.store.dispatch(new DashboardActions.SetCurrentDashboard(currentDashboard));
					}
				});
			}
		});

		this.companiesLoaded$.pipe(takeUntil(this.destroy)).subscribe((companiesLoaded) => {
			this.companiesLoaded = companiesLoaded;
		});

		this.channelsLoaded$.pipe(takeUntil(this.destroy)).subscribe((channelsLoaded) => {
			this.channelsLoaded = channelsLoaded;
		});

		this.districtsLoaded$.pipe(takeUntil(this.destroy)).subscribe((districtsLoaded) => {
			this.districtsLoaded = districtsLoaded;
		});

		this.unitsOfMeasureLoaded$.pipe(takeUntil(this.destroy)).subscribe((unitsOfMeasureLoaded) => {
			this.unitsOfMeasureLoaded = unitsOfMeasureLoaded;
		});

		this.usersLoaded$.pipe(takeUntil(this.destroy)).subscribe((usersLoaded) => {
			this.usersLoaded = usersLoaded;
		});

		this.unitsLoaded$.pipe(takeUntil(this.destroy)).subscribe((unitsLoaded) => {
			this.unitsLoaded = unitsLoaded;
		});

		this.bopsLoaded$.pipe(takeUntil(this.destroy)).subscribe((bopsLoaded) => {
			this.bopsLoaded = bopsLoaded;
		});

		this.devicesLoaded$.pipe(takeUntil(this.destroy)).subscribe((devicesLoaded) => {
			this.devicesLoaded = devicesLoaded;
		});

		this.router.events.pipe(takeUntil(this.destroy)).subscribe((value) => {
			if (value instanceof NavigationEnd) {
				this.searchTerm = '';
				this.url = value.urlAfterRedirects;
				this.mapUrl();
			}
		});

		// Subscribe to route params and job stream together to avoid timing issues
		this.jobRouteParams$.pipe(takeUntil(this.destroy)).subscribe((params) => {
			if (params && params.jobId) {
				// Use isPublic to determine if jobId is a GUID (public) or numeric ID (regular)
				const jobKey = params.jobId; // Keep as string (works for both GUID and numeric)
				const newJobId = this.isPublic ? params.jobId : Number(params.jobId);
				// Only update if the job ID actually changed to avoid unnecessary subscriptions
				if (this.jobId !== newJobId) {
					this.jobId = newJobId;

					// Subscribe to the specific job stream with the correct job key
					this.jobService
						.getJobStream(jobKey)
						.pipe(takeUntil(this.destroy))
						.subscribe((jobUpdate: JobUpdate) => {
							if (jobUpdate) {
								if (this.job && jobUpdate.changeType === ChangeType.SYNC_CHANGE) {
									this.job.lastSyncRequest = jobUpdate.job.lastSyncRequest;
								} else if (jobUpdate.job.key === String(this.jobId) || jobUpdate.job.key === this.jobService.currentJob?.key) {
									this.job = new Job(jobUpdate.job);
									if (this.job.jobStatus?.isCompleted) {
										this.jobCompleted = true;
									}

									if (!this.isPublic && !this.isViewer && this.isSuperAdmin) {
										this.jobService.refreshCurrentJobSplitMergeAvailability();
									}

									this.jobCommentsOld = UtilityHelper.deepCopy(this.job.jobCommentReturn?.jobComments);
									if (this.jobComments && !UtilityHelper.isEqual(this.jobCommentsOld, this.jobComments) && this.commentDrawerSettings?.mode === 'over') {
										this.jobService.commentsChanged = true;
										// this.audioService.playNotificationAudio();
									}
									
									if (this.job?.jobCommentReturn?.jobComments) {
										this.jobComments = UtilityHelper.deepCopy(this.job.jobCommentReturn.jobComments);
									}
								}

								this.isLoggedIn$.pipe(takeUntil(this.destroy)).subscribe((isLoggedIn) => {
									// No need for comment groups to initialize on public links
									if (isLoggedIn) {
										this.dashboardsLoaded$.pipe(takeUntil(this.destroy)).subscribe((dashboardsLoaded) => {
											if (!dashboardsLoaded) {
												if (this.job) {
													this.store.dispatch(new DashboardActions.GetAll({ jobKey: this.job.key, showSpinner: false, showToast: false }));
												}
											}
										});
									}
								});

								this.jobDailyReports = this.job?.jobDailyReports;
								this.determineSyncColor();
								this.changeDetectorRef.detectChanges();
							}
						});
				}
			} else {
				this.jobId = null;
				this.job = null;
			}
		});

		// this.jobService.jobChange$.pipe(takeUntil(this.destroy)).subscribe((jobChange) => {
		// 	if (jobChange) {
		// 		if (this.job && jobChange.changeType === ChangeType.SYNC_CHANGE) {
		// 			this.job.lastSyncRequest = jobChange.job.lastSyncRequest;
		// 		} else if (jobChange.job.key === this.jobService.currentJob?.key) {
		// 			this.job = new Job(jobChange.job);

		// 			if (this.job.jobStatus.isCompleted) {
		// 				this.jobCompleted = true;
		// 			}

		// 			if (!this.isPublic && !this.isViewer && this.isSuperAdmin) {
		// 				this.jobService.refreshCurrentJobSplitMergeAvailability();
		// 			}

		// 			this.jobCommentsOld = UtilityHelper.deepCopy(this.job.jobCommentReturn.jobComments);
		// 			if (this.jobComments && !UtilityHelper.isEqual(this.jobCommentsOld, this.jobComments) && this.commentDrawerSettings?.mode === 'over') {
		// 				this.jobService.commentsChanged = true;
		// 				// this.audioService.playNotificationAudio();
		// 			}

		// 			this.jobComments = UtilityHelper.deepCopy(this.job.jobCommentReturn.jobComments);
		// 		}

		// 		this.isLoggedIn$.pipe(takeUntil(this.destroy)).subscribe((isLoggedIn) => {
		// 			// No need for comment groups to initialize on public links
		// 			if (isLoggedIn) {
		// 				this.dashboardsLoaded$.pipe(takeUntil(this.destroy)).subscribe((dashboardsLoaded) => {
		// 					if (!dashboardsLoaded) {
		// 						if (this.job) {
		// 							this.store.dispatch(new DashboardActions.GetAll({ jobKey: this.job.key, showSpinner: false, showToast: false }));
		// 						}
		// 					}
		// 				});
		// 			}
		// 		});

		// 		this.jobDailyReports = this.job?.jobDailyReports;
		// 		this.determineSyncColor();
		// 		this.changeDetectorRef.detectChanges();
		// 	}
		// });

		this.isSplitMergeAvailable$ = this.jobService.splitMergeState$.pipe(
			map((state) => {
				const currentJobId = this.jobService.currentJob?.id;

				if (state[currentJobId]) {
					return state[currentJobId].isAvailable && !state[currentJobId].isSplitMergeRequestInProgress;
				}

				return false;
			})
		);

		this.dashboardService.dashboardNeedsSaved$.pipe(takeUntil(this.destroy)).subscribe((result) => {
			this.showSaveButton = result;
		});

		this.dashboardService.gridsterConfig$.pipe(takeUntil(this.destroy)).subscribe((gridsterConfig) => {
			if (gridsterConfig) {
				this.isEditMode = gridsterConfig?.draggable?.enabled;
			}
		});

		this.watchSyncColor();

		this.dailyReportService.currentJobDailyReportChanged$.pipe(takeUntil(this.destroy)).subscribe((currentJobDailyReportChanged) => {
			if (currentJobDailyReportChanged) {
				this.dailyReportDate = this.dailyReportService.currentJobDailyReport?.jobDate;
				this.changeDetectorRef.markForCheck();
			}
		});

		this.authService
			.checkAccess([Roles.TenantAdmin, Roles.SuperAdmin], () => {
				return true;
			})
			.pipe(takeUntil(this.destroy))
			.subscribe((isSuperAdmin) => {
				this.isSuperAdmin = isSuperAdmin;
				let companyItem = this.parentViews.find((item) => item.entity === 'Company');
				if (this.isSuperAdmin) {
					companyItem.addLink = '/company/add';
				} else {
					companyItem.addLink = null;
				}
			});

		this.desktopHeaderDisplay = this.layoutConfigService.desktopHeaderDisplay; // objectPath.get(config, 'header.self.fixed.desktop');

		this.jobService.forceMonitorReady.pipe(takeUntil(this.destroy)).subscribe((forceMonitorReady) => {
			this.forceMonitorReady = forceMonitorReady;
		});

		this.completedJobService.showJDRDownload$.pipe(takeUntil(this.destroy)).subscribe((showJDRDownload) => {
			this.showJDRDownload = showJDRDownload;
		});
	}

	ngOnDestroy() {
		// Clear the sync color interval to prevent memory leaks
		if (this.syncColorInterval) {
			clearInterval(this.syncColorInterval);
			this.syncColorInterval = null;
		}
		super.ngOnDestroy();
	}

	editJob() {
		if (this.isPublic) {
			return;
		}

		if (!this.job) {
			const message = `${this.translateService.instant('MODULE.JOB.NOT_READY')}`;
			this.coreService.toast({ message: message, type: StatusType.WARNING });
		}

		const input: JobInfoDialogInputData = {
			key: this.job.key,
			name: this.job.name,
			jobTypeId: this.job.jobTypeId,
			companyId: this.job.companyId,
			wellName: this.job.wellName,
			wellNumber: this.job.wellNumber,
			jobScopeId: this.job.jobScopeId,
			locationId: this.job.location?.id,
			hide: ((this.job.options ?? JobOptions.None) & JobOptions.Hidden) !== 0,
			dateStarted: this.job.start,
			jobStatusId: this.job.status,
			utcDateTimeOffset: this.job.utcDateTimeOffset,
			unitPeriods: this.job.unitPeriods,
			bops: this.job.bops,
		};
		const dialogRef = this.dialog.open(JobEditDialogComponent, {
			data: input,
			width: '100%',
			maxWidth: '500px',
			autoFocus: false,
		});

		dialogRef
			.afterClosed()
			.pipe(takeUntil(this.destroy))
			.subscribe((dialogOutput: JobEditDialogOutputData) => {
				if (!dialogOutput) return;

				if (dialogOutput.action === 'stream-job') {
					const streamJobAction = dialogOutput;
					this.jobService
						.changeUnitPeriodJob({
							unitPeriodId: streamJobAction.unitPeriodId,
							jobId: streamJobAction.jobToStreamToId,
						})
						.pipe(takeUntil(this.destroy))
						.subscribe(() => {
							let filteredActiveJobs = this.filteredActiveJobs.filter((faj) => faj.id !== this.job.id);
							this.store.dispatch(new JobActions.SetAllActiveJobs(filteredActiveJobs));
							const message = `${this.translateService.instant('MODULE.UNIT.UNIT_DATA_MOVED')}`;
							this.coreService.toast({ message: message, type: StatusType.SUCCESS });
							this.jobService.refreshJob(String(streamJobAction.jobToStreamToId), true, false);
							this.router.navigateByUrl('/job/' + String(streamJobAction.jobToStreamToId));
						});
				} else if (dialogOutput.save) {
					this.jobService
						.editJobInformation(dialogOutput.editJobInformationPayload)
						.pipe(takeUntil(this.destroy))
						.subscribe((job) => {
							// TODO: modifying the cache should really be done inside the service...
							this.jobService.modifyCachedJob(job);
						});
				}
			});
	}

	editJobAlerts() {
		if (this.isPublic) return;

		const dialogRef = this.dialog.open(JobAlertsEditDialogComponent, {
			data: {
				key: this.job.key,
			},
			width: '100%',
			maxWidth: '1000px',
			autoFocus: false,
		});

		dialogRef
			.afterClosed()
			.pipe(takeUntil(this.destroy))
			.subscribe((data: { key: string; jobChannels: UnitPeriodChannelDto[] }) => {
				if (data) {
					this.jobService.updateJobChannels(data.key, data.jobChannels);
				}
			});
	}

	multiStream() {
		let job = this.jobService.currentJob;

		if (!job) return;

		const dialogRef = this.dialog.open(JobMultiStreamDialogComponent, {
			data: { job: job },
			width: '100%',
			maxWidth: '650px',
			autoFocus: false,
		});

		dialogRef
			.afterClosed()
			.pipe(takeUntil(this.destroy))
			.subscribe((data) => {
				if (!data) return;
			});
	}

	endJob() {
		if (!this.isViewer) {
			// Can only end a job on viewer
			return;
		}

		let job = this.jobService.currentJob;

		const confirmModel: ConfirmDialogModel = {
			title: 'End job',
			message: 'Are you sure you want to end this job?',
			action: 'End',
		};

		const dialogRef = this.dialogService.confirmAction(confirmModel);

		dialogRef
			.afterClosed()
			.pipe(takeUntil(this.destroy))
			.subscribe((result) => {
				if (result) {
					this.jobService
						.endJob(job, true)
						.pipe(takeUntil(this.destroy))
						.subscribe(
							() => {
								let jobCached = this.jobService.jobsCache.find((cachedJob) => cachedJob.id === job.id);
								jobCached.status = JobStatus.Completed;
								this.store.dispatch(new JobActions.RemoveActiveJob(job));
								this.store.dispatch(new JobActions.GetAllCompletedJobs()); // Refreshing completed jobs since this ended job will stay there for a short period of time
								const message = `${this.translateService.instant('GENERIC.ENDED', { name: this.translateService.instant('MODULE.JOB.TITLE_CAPITAL') })}`;
								this.coreService.toast({ message: message, type: StatusType.SUCCESS });
								this.router.navigateByUrl('/job');
							},
							(error) => {
								const message = `${this.translateService.instant('GENERIC.NOT_ENDED', { name: this.translateService.instant('MODULE.JOB.TITLE_CAPITAL') })}`;
								this.coreService.toast({ message: message, type: StatusType.SUCCESS });
							}
						);
				}
			});
	}

	openJobLinkListModal() {
		this.dialog.open(JobLinksDialogComponent, {
			data: { key: this.job.key },
			width: '100%',
			maxWidth: '1000px',
			autoFocus: false,
		});
	}

	openRunningFootageDialog() {
		const dialogRef = this.dialog.open(RunningFootageDialogComponent, {
			data: { job: this.job, upid: this.job.unitPeriods[0].id },
			width: '100%',
			maxWidth: '400px',
			autoFocus: false,
		});

		dialogRef
			.afterClosed()
			.pipe(takeUntil(this.destroy))
			.subscribe(() => {});
	}

	mapUrl() {
		this.isJobPage = false;

		// For pages with custom subheader
		if (this.url.includes('job/')) {
			this.isJobPage = true;
			if (this.url.includes('/force-monitor')) {
				this.pageView = 'force-monitor';
			} else if (this.url.includes('/dashboard')) {
				this.pageView = 'dashboard';
			} else if (this.url.includes('/daily-reports')) {
				this.pageView = 'daily-reports';
			} else if (this.url.includes('/raw-data')) {
				this.pageView = 'raw-data';
			} else if (this.url.includes('/attachments')) {
				this.pageView = 'attachments';
			} else if (this.url.includes('/custom-charts')) {
				this.pageView = 'custom-charts';
			} else if (this.url.includes('/bop-inspection')) {
				this.pageView = 'custom-charts';
			} else if (this.url.includes('/complete')) {
				this.pageView = 'complete';
				this.isJobPage = false;
			} else {
				this.pageView = 'overview';
			}
		} else if (this.url.includes('eula')) {
			this.pageView = 'eula';
		} else {
			this.pageView = '';
		}

		// For pages with regular subheader
		if (this.url.includes('/job')) {
			this.parentRoute = 'job';
		} else if (this.url.includes('/channel')) {
			this.parentRoute = 'channel';
		} else if (this.url.includes('/comment')) {
			this.parentRoute = 'comment';
		} else if (this.url.includes('/company')) {
			this.parentRoute = 'company';
		} else if (this.url.includes('/district')) {
			this.parentRoute = 'district';
		} else if (this.url.includes('/unit-config')) {
			this.parentRoute = 'unit-config';
		} else if (this.url.includes('/output-config')) {
			this.parentRoute = 'output-config';
		} else if (this.url.includes('/user')) {
			this.parentRoute = 'user';
		} else if (this.url.includes('/asset/unit')) {
			this.parentRoute = 'asset/unit';
		} else if (this.url.includes('/asset/string')) {
			this.parentRoute = 'asset/string';
		} else if (this.url.includes('/asset/reel')) {
			this.parentRoute = 'asset/reel';
		} else if (this.url.includes('/asset/bop')) {
			this.parentRoute = 'asset/bop';
		} else if (this.url.includes('/asset/device')) {
			this.parentRoute = 'asset/device';
		} else if (this.url.includes('/unit-of-measure')) {
			this.parentRoute = 'unit-of-measure';
		} else if (this.url.includes('/unit')) {
			this.parentRoute = 'unit';
		} else if (this.url.includes('/unit-config')) {
			this.parentRoute = 'unit';
		} else if (this.url.includes('/output-config')) {
			this.parentRoute = 'unit';
		} else if (this.url.includes('/download')) {
			this.parentRoute = 'download';
		} else if (this.url.includes('/version')) {
			this.parentRoute = 'version';
		} else if (this.url.includes('/eula')) {
			this.parentRoute = 'eula';
		} else if (this.url.includes('/error-page')) {
			this.parentRoute = 'error-page';
		}

		// Checks if the url is the root. If not, we hide search and add button
		if (this.url === `/${this.parentRoute}`) {
			this.isRoot = true;
		} else {
			this.isRoot = false;
		}

		// Resetting current job when navigating away
		if (!this.isJobPage) {
			this.job = null;
		}

		this.selectedParentView = this.parentViews.find((parentView) => parentView.route === this.parentRoute);
	}

	newGuid(): string {
		return UtilityHelper.newGuid();
	}

	toggleCommentDrawer() {
		this.drawerService.toggleCommentDrawer();
		this.jobService.commentsChanged = false;
	}

	toggleCommentMobile() {
		this.jobService.addComment = false;
		this.jobService.commentMode = !this.jobService.commentMode;
		this.showCommentDropdown = false;
		this.jobService.commentsChanged = false;
	}

	toggleModelDrawer() {
		this.drawerService.toggleModelDrawer();
	}

	search() {
		this.subheaderService.searchForTerm(this.searchTerm);
	}

	dynamicItemsReady(selectedParentView) {
		if (selectedParentView.entity === 'Company') {
			return this.companiesLoaded;
		} else if (selectedParentView.entity === 'Channel') {
			return this.channelsLoaded;
		} else if (selectedParentView.entity === 'District') {
			return this.districtsLoaded;
		} else if (selectedParentView.entity === 'Unit of Measure') {
			return this.unitsOfMeasureLoaded;
		} else if (selectedParentView.entity === 'User') {
			return this.usersLoaded;
		} else if (selectedParentView.entity === 'Unit') {
			return this.unitsLoaded;
		} else if (selectedParentView.entity === 'BOP') {
			return this.bopsLoaded;
		} else if (selectedParentView.entity === 'Device') {
			return this.devicesLoaded;
		} else {
			return true;
		}
	}

	triggerCompletedJobSearch() {
		this.completedJobService.triggerSearch();
	}

	triggerJDRDownload() {
		this.completedJobService.triggerJDRDownload();
	}

	filterJobsByServiceLine(jobType: JobTypeDto) {
		jobType.checked = !jobType.checked;
		let checkedJobTypeIds: number[] = this.jobTypes.filter((jobType) => jobType.checked).map((jobType) => jobType.id);
		this.store.dispatch(new StorageActions.SetSelectedFilterJobTypeIds(checkedJobTypeIds));
	}

	downloadJdr() {
		this.spinnerService.showAppSpinner();
		this.dailyReportService
			.downloadDailyReport(this.job.key)
			.pipe(takeUntil(this.destroy))
			.subscribe((result) => {
				if (result.type != 'text/plain') {
					var blob = new Blob([result]);
					let file = 'job-detail-report.pdf';
					saveAs(blob, file);
					const message = `${this.translateService.instant('GENERIC.DOWNLOADED', { name: this.translateService.instant('MODULE.JDR.TITLE') })}`;
					this.coreService.toast({ message: message, type: StatusType.SUCCESS });
				} else {
					const message = `${this.translateService.instant('GENERIC.NOT_DOWNLOADED', { name: this.translateService.instant('MODULE.JDR.TITLE') })}`;
					this.coreService.toast({ message: message, type: StatusType.DANGER });
				}

				this.spinnerService.hideAppSpinner();
			});
	}

	emailJdr() {
		const dialogRef = this.dialog.open(EmailJdrDialogComponent, {
			data: {
				job: this.job,
			},
			width: '100%',
			maxWidth: '400px',
			autoFocus: false,
		});

		dialogRef
			.afterClosed()
			.pipe(takeUntil(this.destroy))
			.subscribe(() => {});
	}

	filterCommentList(groupId: number) {
		this.selectedJobCommentDefaultGroup = this.job.jobCommentReturn.filteredJobCommentDefaultGroups.find((filteredJobCommentDefaultGroup) => filteredJobCommentDefaultGroup.id === groupId);
	}

	addJobComment(jobCommentDefault?: JobCommentDefaultDto) {
		if (!jobCommentDefault) {
			jobCommentDefault = {
				id: 0,
				comment: '',
				jobCommentDefaultGroupId: 0,
			};
		}

		let jobCommentDto: JobCommentDto = {
			id: 0,
			jobId: this.job.id,
			comment: '',
			overrideComment: jobCommentDefault.overrideComment,
			commentDate: new Date(),
			jobCommentDefaultId: jobCommentDefault.id,
			jobCommentDefault: jobCommentDefault,
			hideInPortal: false,
			replacements: '',
		};

		this.openCommentAddDialog(jobCommentDto);
	}

	openCommentAddDialog(jobCommentDto: JobCommentDto) {
		const dialogRef = this.dialog.open(CommentAddDialogComponent, {
			data: {
				jobComment: jobCommentDto,
				job: this.job,
				source: 'widget-dashboard',
			},
			width: '100%',
			maxWidth: this.isViewer ? '500px' : '450px',
			panelClass: 'comment-add-dialog-wrap',
			autoFocus: false,
		});

		dialogRef
			.afterClosed()
			.pipe(takeUntil(this.destroy))
			.subscribe((response) => {
				if (response?.action === 'insert') {
					this.spinnerService.showAppSpinner(); // Turns on app wide spinner
					this.jobService._postJobComment(this.job.key, response.jobComment).subscribe((newComment: JobCommentDto) => {
						let attachmentIds = response.jobComment.jobAttachments.map((jobAttachment) => {
							return jobAttachment.id;
						});
						this.jobService.addJobAttachmentsToComment(this.job.id, newComment.id, attachmentIds);
						// Pressure Test - If criteria met, automatically generate a
						// specific custom chart attachment, behind the scenes.
						this.checkToAutoGeneratePressureTestChart(this.job, jobCommentDto);
						this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					});
				}
			});
	}

	syncJob(syncType: 'all' | 'latest') {
		if (this.syncService.isConnected) {
			this.syncService.syncJob(this.job, syncType, true, true);
		}
	}

	determineSyncColor() {
		if (this.job?.lastSyncRequest) {
			var currentDate = new Date();
			var lastSyncRequestDate = new Date(this.job.lastSyncRequest);

			this.syncDiff = differenceInMinutes(currentDate, lastSyncRequestDate);

			if (this.syncDiff < 5) {
				this.syncColor = 'green';
			} else if (this.syncDiff < 15) {
				this.syncColor = 'yellow';
			} else {
				this.syncColor = 'red';
			}

			var lastSyncedTimeDisplay = this.getTimeDiff(lastSyncRequestDate, currentDate);
			this.syncDisplay = lastSyncedTimeDisplay ? 'Last synced ' + lastSyncedTimeDisplay + ' ago' : 'Sync up to date';
		} else {
			this.syncDiff = null;
			this.syncColor = 'red';
			this.syncDisplay = 'job has not synced';
		}
	}

	watchSyncColor() {
		this.syncColorInterval = setInterval(() => {
			this.determineSyncColor();
		}, 60000);
	}

	openAddDailyDialog() {
		let dialogRef = this.dialog.open(AddDailyReportDialogComponent, {
			data: {},
			width: '100%',
			maxWidth: '350px',
			panelClass: 'dailyReportComponentModal',
			autoFocus: false,
		});

		dialogRef
			.afterClosed()
			.pipe(takeUntil(this.destroy))
			.subscribe((result) => {
				if (result) {
					let jobDailyReport: JobDailyReportDto = {
						id: 0,
						jobDate: result,
						jobId: parseInt(this.job.key),
						isDeleted: false,
					};

					this.dailyReportService
						.addJobDailyReport(this.job.key, jobDailyReport)
						.pipe(takeUntil(this.destroy))
						.subscribe((result) => {
							this.dailyReportService.setCurrentJobDailyReport(result);
						});
				}
			});
	}

	changeDailyReport(dailyReport: JobDailyReportDto) {
		this.router.navigateByUrl(`/job/${dailyReport.job.key}/daily-reports/${dailyReport.id}`);
	}

	/** Dashboard Start */

	openEditWidgetDialog() {
		if (this.currentDashboard?.isGlobal) return;

		let widgetEditDialogRef = this.dialog.open(WidgetEditDialogComponent, {
			data: {
				key: this.job.key,
				gridsterItem: {},
			},
			width: '100%',
			maxWidth: '350px',
			autoFocus: false,
		});

		widgetEditDialogRef
			.afterClosed()
			.pipe(takeUntil(this.destroy))
			.subscribe((result: GridsterItem) => {
				if (result) {
					let cols = 25;
					let rows = 25;

					if (result.dashboardItemTypeId === 1) {
					} else if (result.dashboardItemTypeId === 2) {
						// Text Display
						cols = 20;
						rows = 20;
					} else if (result.dashboardItemTypeId === 3) {
					} else if (result.dashboardItemTypeId === 4) {
						// Job Chart
						cols = 60;
						rows = 40;
					} else if (result.dashboardItemTypeId === 5) {
					} else if (result.dashboardItemTypeId === 6) {
						// Channel display
						cols = 50;
						rows = 30;
					}

					const dashboardItemDto: DashboardItemDto = {
						cols: cols,
						rows: rows,
						positionY: 0,
						positionX: 0,
						chartName: this.newGuid(),
						dashboardItemTypeId: result.dashboardItemTypeId,
						id: 0,
						dashboardId: this.currentDashboard.id,
						channelFontSize: result.channelFontSize,
						valueFontSize: result.valueFontSize,
						disableFlashing: false,
						disableSounds: false,
						channelId: result.channelId,
						min: result.min,
						max: result.max,
						additionalJsonSettings: '',
					};

					this.dashboardService.itemAdded(dashboardItemDto);
				}
			});
	}

	openNewDashboardDialog(isNew: boolean) {
		let dialogRef = this.dialog.open(NamingDialogComponent, {
			data: {
				title: isNew ? 'New Dashboard' : `Save Dashboard '${this.currentDashboard.name}' as`,
			},
			width: '100%',
			maxWidth: '400px',
			autoFocus: false,
		});

		dialogRef
			.afterClosed()
			.pipe(takeUntil(this.destroy))
			.subscribe((result) => {
				if (result) {
					const dashboardDto: DashboardDto = {
						id: 0,
						name: result,
						isGlobal: false,
						dashboardItems: [],
					};

					if (isNew) {
						this.store.dispatch(new DashboardActions.CreateDashboard({ jobKey: this.job.key, dashboard: dashboardDto }));

						let subscription: Subscription = this.actions$.pipe(ofActionCompleted(DashboardActions.CreateDashboardSuccess)).subscribe((response) => {
							this.store.dispatch(new DashboardActions.SetCurrentDashboard(response.action.dashboard));
							subscription.unsubscribe();
						});
					} else {
						this.triggerDashboardSaveAs(dashboardDto.name);
					}
				}
			});
	}

	openSelectDashboardDialog() {
		const dialogRef = this.dialog.open(SelectDashboardDialogComponent, {
			data: {
				jobKey: this.job?.key,
			},
			width: '100%',
			maxWidth: '500px',
			autoFocus: false,
		});

		dialogRef
			.afterClosed()
			.pipe(takeUntil(this.destroy))
			.subscribe((dashboard: DashboardDto) => {
				if (dashboard) {
					let doOpen = () => {
						this.store.dispatch(new DashboardActions.SetCurrentDashboard(dashboard));
					};

					if (this.showSaveButton) {
						const confirmModel: ConfirmDialogModel = {
							title: 'Save dashboard changes',
							message: 'You have unsaved changes, would you like to save?',
							action: this.translateService.instant('ACTION.SAVE_CAPITAL'),
						};

						const dialogRef = this.dialogService.confirmAction(confirmModel);

						dialogRef
							.afterClosed()
							.pipe(takeUntil(this.destroy))
							.subscribe((result) => {
								if (result) {
									this.dashboardService.triggerSave();
									doOpen();
								} else {
									doOpen();
									this.showSaveButton = false;
								}
							});
					} else {
						doOpen();
					}
				}
			});
	}

	triggerDashboardEdit() {
		this.dashboardService.editDashboardMode();
	}

	triggerDashboardEditCancel() {
		// TODO revert gridster items if not saved and only cancelled
		this.dashboardService.editDashboardModeCancel();
	}

	triggerDashboardSave() {
		if (this.showSaveButton) {
			this.dashboardService.triggerSave();
			this.showDashboardMenu = false;
		} else {
			this.triggerDashboardEditCancel();
		}
	}

	triggerDashboardSaveAs(dashboardName: string) {
		this.dashboardService.triggerSaveAs(dashboardName);
		this.showDashboardMenu = false;
	}

	openUserDashboardSettingsDialog() {
		this.dialog.open(DashboardSettingDialogComponent, {
			data: {
				userDashboardSettings: this.userDashboardSettings,
			},
			width: '100%',
			maxWidth: '400px',
			autoFocus: false,
		});
	}

	openUserDashboardDeleteDialog() {}

	/** Dashboard End */

	openSplitMergeDialog(splitType: string) {
		const dialogRef = this.dialog.open(JobSplitMergeDialogComponent, {
			data: {
				splitType: splitType,
				fromJob: this.job,
				startDate: this.dateRangeService.currentDateRange?.startDate ?? this.job.start,
				endDate: this.dateRangeService.currentDateRange?.endDate ?? this.job.end,
			},
			width: '100%',
			maxWidth: '1000px',
			autoFocus: false,
		});

		dialogRef
			.afterClosed()
			.pipe(takeUntil(this.destroy))
			.subscribe((data: { jobData: JobEditData }) => {
				if (!data) return;

				// this.jobService.editJobInformation(data.jobData).pipe(takeUntil(this.destroy)).subscribe(job => {
				// 	this.jobService.modifyCachedJob(job);
				// 	this.selectJob(job);
				// });
			});
	}

	unitPeriodSelected(unitPeriod: UnitPeriodDto) {
		this.jobService.triggerUnitPeriodChangeEvent(unitPeriod);
	}

	openChannelMappingModal() {
		const dialogRef = this.dialog.open(JobChannelMappingDialogComponent, {
			data: {
				job: this.job,
			},
			width: '100%',
			maxWidth: '600px',
			panelClass: 'jobChannelMapping',
			autoFocus: false,
		});

		dialogRef
			.afterClosed()
			.pipe(takeUntil(this.destroy))
			.subscribe((data: { jobData: JobEditData }) => {
				if (!data) return;
			});
	}

	/**
	 * Gets the difference in time as a nice, formatted string based off of the Start and End Date of axis
	 */
	getTimeDiff(startDate: Date, endDate: Date): string {
		if (!startDate || !endDate) return '--';

		let totalSeconds = differenceInSeconds(endDate, startDate); // seconds total
		let totalDays = Math.floor(totalSeconds / (60 * 60 * 24));
		totalSeconds %= 60 * 60 * 24;
		let totalHours = Math.floor(totalSeconds / (60 * 60));
		totalSeconds %= 60 * 60;
		let totalMinutes = Math.floor(totalSeconds / 60);
		totalSeconds %= 60;

		let totalTime: string = '';
		if (totalDays > 0) totalTime += `${totalDays}d `;
		if (totalHours > 0) totalTime += `${totalHours}h `;
		if (totalMinutes > 0) totalTime += `${totalMinutes}m `;

		return totalTime;
	}

	async checkToAutoGeneratePressureTestChart(job: Job, comment: JobCommentDto) {
		// Initial check to see if the comment that was just added is of type END_PRESSURE_TESTING.
		if (!this.isJobCommentOfType(comment, this.JobType.END_PRESSURE_TESTING)) {
			return;
		}

		// Examine list of comments to ensure that we can reasonably & safely (can't do it precisely)
		// match up an End Pressure Test comment with a Start Pressure Test Comment, that doesn't
		// already have a match.
		const comments: JobCommentDto[] = this.getActivePressureStartAndEndCommentsByJob(job);
		const getCommentCountByType = (commentType) => {
			return comments.filter((c: JobCommentDto) => this.isJobCommentOfType(c, commentType)).length;
		};
		const isThereOneMoreStartTestThanEndTest = () => {
			const numberOfStarts: number = getCommentCountByType(this.JobType.START_PRESSURE_TESTING);
			const numberOfEnds: number = getCommentCountByType(this.JobType.END_PRESSURE_TESTING);
			return numberOfStarts === numberOfEnds + 1;
		};

		// Check to see if the following criteria is met:
		//  - First comment in the list must be a start; in this context,
		//  	this is the most recent comment by 'commentDate'.
		//	- Within the list, there should be 1 more start than end.
		const isMostRecentCommentStartPressureTest: boolean = first(comments)?.jobCommentDefault.comment === this.JobType.START_PRESSURE_TESTING;
		const areAutoGenCriteriaMet: boolean = isMostRecentCommentStartPressureTest && isThereOneMoreStartTestThanEndTest();

		if (areAutoGenCriteriaMet) {
			await this.autoGenerateCustomChartAttachment();
		}
	}

	async autoGenerateCustomChartAttachment() {
		const canDestroyGetModel = new Subject<void>();
		const canDestroySaveAttachments = new Subject<void>();
		this.jobService
			.getJob(String(this.job.id), true, false)
			.pipe(takeUntil(this.destroy))
			.subscribe({
				next: (job) => {
					const comments: JobCommentDto[] = this.getLatestStartAndEndPressureTestingComments(job);
					const { startDate, endDate, chartInitAnnotations } = this.getDatesAndAnnotations(comments);
					const chartAnnotations = UtilityHelper.deepCopy(chartInitAnnotations);
					const customChartInitModel: CustomChartInitModel = {
						jobId: job.id,
						customChartSettingsType: 'auto-gen-pressure-test',
						annotations: chartInitAnnotations,
						startDate,
						endDate,
					};

					this.customChartsInitService
						.getCustomChartModel(customChartInitModel)
						.pipe(takeUntil(canDestroyGetModel))
						.subscribe(async (customChartModel: CustomChartModel) => {
							if (customChartModel) {
								canDestroyGetModel.next();
								canDestroyGetModel.complete();
								const { chart, chartSettings } = customChartModel;
								const message = `Creating pressure test attachment...`;
								this.coreService.toast({ message: message, type: StatusType.SUCCESS, duration: 3500 });
								// Render chart to hidden DOM element in order to auto-generate
								// the attachment without any display within the UI.
								this.showChart = true;
								this.chart = chart;
								await UtilityHelper.waitForProperty(this, 'chart.ref', 30, 100);
								// Update annotations with correct y-axis minimum
								const updatedAnnotations = this.updateAnnotationsWithChartYMin(chartAnnotations);
								const { pdf } = await this.getCustomChartPdf(chartSettings, startDate, endDate, job, updatedAnnotations);

								this.customChartsInitService
									.saveToAttachments({
										action: this.translateService.instant('ACTION.SAVE_CAPITAL'),
										file: pdf,
									})
									.pipe(takeUntil(canDestroySaveAttachments))
									.subscribe(() => {
										canDestroySaveAttachments.next();
										canDestroySaveAttachments.complete();
										this.chart = undefined;
										this.showChart = false;
										this.customChartService.chartSettings.next([]);
										const message = `... attachment successfully created`;
										this.coreService.toast({ message: message, type: StatusType.SUCCESS, duration: 3500 });
									});
							}
						});
				},
				error: () => {},
			});
	}

	async getCustomChartPdf(chartSettings: ChartSettingsModel, startDate: Date, endDate: Date, job: Job, annotations?: AnnotationsOptions[]): Promise<CustomChartAttachmentResponse> {
		return await this.customChartsSaveAsAttachmentService.generateCustomChartAttachment({
			job,
			chart: this.chart,
			title: chartSettings.title,
			companyName: this.job?.company?.name,
			wellName: this.job?.wellName,
			wellNumber: this.job?.wellNumber,
			chartSettings,
			startDate,
			endDate,
			orientation: 'landscape',
			annotations: annotations ?? null,
		});
	}

	private getDatesAndAnnotations(comments: JobCommentDto[]): AutoGenDatesAndAnnotations {
		const { [0]: end, [1]: start } = comments;

		// Per user requirements, add a five-minute buffer to the
		// start and end comments, so that they both show up cleanly
		// on the chart, with room to spare.
		const startDate: Date = subMinutes(start.commentDate, 5);
		const endDate: Date = addMinutes(end.commentDate, 5);

		// Create individual annotations, one for each comment
		const chartInitAnnotations: AnnotationsOptions[] = orderBy(comments, ['commentDate'], ['asc']).map((comment: JobCommentDto) => ({
			id: UtilityHelper.generateUuid(),
			labels: [
				{
					point: {
						x: new Date(comment.commentDate).getTime(),
						y: 0,
						xAxis: 0,
						yAxis: 0,
					},
					text: comment.comment,
					y: -15,
				},
			],
			labelOptions: {
				allowOverlap: true,
				backgroundColor: 'white',
				borderColor: '#e0e0e0',
				padding: 13,
				borderRadius: 4,
				style: {
					fontSize: '16px',
					width: 150,
					color: '#ffffff',
				},
			},
		}));

		return { startDate, endDate, chartInitAnnotations };
	}

	private updateAnnotationsWithChartYMin(annotations: AnnotationsOptions[]): AnnotationsOptions[] {
		if (!this.chart?.ref) return annotations;

		const chartReference = this.chart.ref as Highcharts.Chart;
		const yAxisMin = chartReference.yAxis[0].min;

		return annotations.map((annotation) => ({
			...annotation,
			labels: annotation.labels.map((label) => ({
				...label,
				point: {
					//@ts-ignore
					...label.point,
					y: yAxisMin,
				},
			})),
		}));
	}

	getLatestStartAndEndPressureTestingComments(job: Job): JobCommentDto[] {
		// From the latest filtered, sorted comments, check the last two
		// to ensure that the last one is end, and the second-to-last one
		// is start. Return these last two comments, if that is the case.
		const latestStartAndEndComments: JobCommentDto[] = takeRight(this.getActivePressureStartAndEndCommentsByJob(job) || [], 2);
		const isLatestCommentEnd: boolean = latestStartAndEndComments[0].jobCommentDefault.comment === this.JobType.END_PRESSURE_TESTING;
		const isSecondToLastCommentStart: boolean = latestStartAndEndComments[1].jobCommentDefault.comment === this.JobType.START_PRESSURE_TESTING;

		return isLatestCommentEnd && isSecondToLastCommentStart ? latestStartAndEndComments : null;
	}

	getActivePressureStartAndEndCommentsByJob(job: Job): JobCommentDto[] {
		let comments: JobCommentDto[] = [...job?.jobCommentReturn?.jobComments];

		if (!comments || !comments.length) {
			return comments;
		}

		const isPressureStartOrEnd = (typeString: string): boolean => {
			return [this.JobType.START_PRESSURE_TESTING, this.JobType.END_PRESSURE_TESTING].includes(typeString);
		};

		// Filter comments to active (deleted: false), and type of 'start' or 'end' pressure test.
		comments = comments.filter((comment: JobCommentDto) => comment?.jobCommentDefault?.comment && isPressureStartOrEnd(comment.jobCommentDefault.comment)).filter((comment: JobCommentDto) => !comment.isDeleted);

		if (comments.length) {
			comments = orderBy(comments, ['commentDate'], ['desc']);
		}

		return comments;
	}

	isJobCommentOfType(comment: JobCommentDto, type: string): boolean {
		return comment.jobCommentDefault.comment === type;
	}

	get getPageTitle() {
		let pageTitle: string;
		if (this.parentRoute === 'eula') {
			pageTitle = 'End User License Agreement';
		} else if (this.parentRoute === 'job') {
			if (!this.pageView) {
				pageTitle = 'Active Jobs';
			} else {
				if (this.pageView === 'complete') {
					pageTitle = 'Completed Jobs';
				} else {
				}
			}
		} else if (this.selectedParentView && !this.selectedParentView?.customDisplay && !this.pageView) {
			pageTitle = this.selectedParentView?.display;
		} else {
			pageTitle = '';
		}

		return pageTitle;
	}
}
